import { useEffect, useMemo, useState } from 'react'
import { MapContainer, Marker, TileLayer, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { MapData, MapPoint } from '../api/types'

/** Midt i Danmark - kun når der ikke er et eneste punkt at zoome til. */
const DENMARK: [number, number] = [56.1, 10.2]

/**
 * Over så mange nåle bliver navnene en ulæselig klump på afstand. Så står nålene
 * alene, og farven (status, rød = overskredet) må bære budskabet.
 */
const MAX_LABELS = 30

/**
 * Kortet over dashboardets åbne sager på TV-skærmen (beslutning 111).
 *
 * Ingen kan klikke på et TV, så kortet er låst: ingen zoomknapper, intet træk.
 * Det zoomer selv ud til alle nålene - og gør det igen, når der kommer en sag til
 * eller forsvinder, så en ny sag i Aalborg ikke står uden for billedet.
 *
 * Punkterne kommer med dashboardets egne data og bygges af samme regel som
 * kortsiden, så TV'et kun behøver sit enhedstoken - aldrig brugerlogin.
 */
export function TvMap({ map: data }: { map: MapData }) {
  const [map, setMap] = useState<L.Map | null>(null)
  const points = data.points

  // Nøglen er koordinaterne, ikke listen: listen er ny ved hver opdatering fra
  // serveren, og så ville kortet zoome forfra hver gang, selv om intet har flyttet sig.
  const key = points.map((p) => `${p.latitude},${p.longitude}`).join('|')
  const bounds = useMemo(
    () => (key === '' ? null : L.latLngBounds(key.split('|').map((c) => c.split(',').map(Number) as [number, number])).pad(0.12)),
    [key]
  )

  // Samme forbehold som på kortsiden: Leaflet kan ikke regne et zoomniveau ud,
  // før containeren har en størrelse. Her følger kortet dog med ved HVER
  // størrelsesændring - der er ingen bruger, der har zoomet selv.
  useEffect(() => {
    if (!map || !bounds) return

    const element = map.getContainer()
    const apply = () => {
      if (element.clientWidth === 0 || element.clientHeight === 0) return
      map.invalidateSize()
      map.fitBounds(bounds, { maxZoom: 13 })
    }

    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(element)
    return () => observer.disconnect()
  }, [map, bounds])

  const missing = data.missingAddress + data.missingPosition + data.awaitingLookup
  const spots = useMemo(() => groupBySpot(points), [points])
  const showLabels = spots.length <= MAX_LABELS

  return (
    <div className="tv__map">
      <MapContainer
        center={DENMARK}
        zoom={7}
        ref={setMap}
        zoomControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        keyboard={false}
        boxZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          // OpenStreetMap kræver, at der står hvor fliserne kommer fra.
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {spots.map((spot) => (
          <Marker
            key={spot.key}
            position={[spot.first.latitude, spot.first.longitude]}
            icon={iconFor(spot.isOverdue ? '#DC2626' : spot.first.statusColor)}
            // De overskredne øverst - ellers kan en rød nål gemme sig under en grå.
            zIndexOffset={spot.isOverdue ? 1000 : 0}
          >
            {showLabels && (
              <Tooltip permanent direction="right" offset={[14, -18]} className="tv__map-label">
                {spot.first.name}
                {spot.points.length > 1 && <span className="tv__map-more"> +{spot.points.length - 1}</span>}
              </Tooltip>
            )}
          </Marker>
        ))}
      </MapContainer>

      {points.length === 0 && (
        <div className="tv__map-empty">Ingen åbne sager med en adresse på kortet.</div>
      )}

      {/* Hullerne skal siges - ellers ser kortet komplet ud, selv om der mangler sager på det. */}
      {missing > 0 && (
        <div className="tv__map-missing">
          {missing} {missing === 1 ? 'sag mangler' : 'sager mangler'} på kortet - adressen mangler eller er ikke slået op endnu
        </div>
      )}
    </div>
  )
}

interface Spot {
  key: string
  first: MapPoint
  points: MapPoint[]
  isOverdue: boolean
}

/**
 * Sager på samme adresse bliver til ÉN nål med "+N". Hos en kunde med fem sager
 * lå fem nåle og fem navne oven i hinanden, og intet af det kunne læses. Står der
 * en overskreden sag på stedet, er den navnet på nålen, og nålen er rød.
 */
function groupBySpot(points: MapPoint[]): Spot[] {
  const spots = new Map<string, Spot>()

  for (const point of points) {
    const key = `${point.latitude},${point.longitude}`
    const spot = spots.get(key)
    if (!spot) {
      spots.set(key, { key, first: point, points: [point], isOverdue: point.isOverdue })
      continue
    }
    spot.points.push(point)
    if (point.isOverdue && !spot.isOverdue) {
      spot.first = point
      spot.isOverdue = true
    }
  }

  return [...spots.values()]
}

/** Samme nål som på kortsiden, bare større, så den kan ses på afstand. */
function iconFor(color: string) {

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="47" viewBox="0 0 26 34">
      <path d="M13 33C13 33 25 20.5 25 13A12 12 0 1 0 1 13c0 7.5 12 20 12 20z"
            fill="${color}" stroke="#fff" stroke-width="2"/>
      <circle cx="13" cy="13" r="4.5" fill="#fff"/>
    </svg>`

  return L.divIcon({
    html: svg,
    className: 'map-pin',
    iconSize: [36, 47],
    iconAnchor: [18, 46],
    tooltipAnchor: [0, 0]
  })
}
